// ./src/components/generic/MonthDaySelector/MonthDaySelector.tsx
import React, { useCallback, useMemo } from "react";
import {
  UnstyledButton,
  Text,
  Input,
  Stack,
  SimpleGrid,
  Switch,
  Box,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

// ============================================
// Types
// ============================================

export interface MonthDaySelectorProps {
  /** Selected days (1-31 or "last") */
  value: string[];

  /** Callback when selection changes */
  onChange: (value: string[]) => void;

  /** Optional label */
  label?: string;

  /** Optional description */
  description?: string;

  /** Error message */
  error?: string;

  /** Whether the field is required */
  required?: boolean;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size variant */
  size?: "xs" | "sm" | "md" | "lg";

  /** Whether to allow multiple selection (default: true) */
  multiple?: boolean;

  /** Whether to show the "Last day of month" option (default: true) */
  showLastDay?: boolean;
}

// ============================================
// Constants
// ============================================

// Days 1-31
const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

// ============================================
// Component
// ============================================

const MonthDaySelectorComponent: React.FC<MonthDaySelectorProps> = ({
  value,
  onChange,
  label,
  description,
  error,
  required = false,
  disabled = false,
  size = "md",
  multiple = true,
  showLastDay = true,
}) => {
  const theme = useMantineTheme();
  const { t } = useTranslation(["common"]);

  // Check if "last" is selected
  const hasLastDay = useMemo(() => value.includes("last"), [value]);

  // Numeric days only (for grid display)
  const numericDays = useMemo(
    () => value.filter((d) => d !== "last").map((d) => parseInt(d, 10)),
    [value]
  );

  // Handle day toggle
  const handleDayClick = useCallback(
    (day: number) => {
      if (disabled) return;

      const dayStr = String(day);

      if (multiple) {
        const isSelected = numericDays.includes(day);
        let newDays: string[];

        if (isSelected) {
          newDays = value.filter((d) => d !== dayStr);
        } else {
          // Add and sort numerically, keeping "last" at the end
          const newNumeric = [...numericDays, day].sort((a, b) => a - b);
          newDays = newNumeric.map(String);
          if (hasLastDay) {
            newDays.push("last");
          }
        }
        onChange(newDays);
      } else {
        onChange([dayStr]);
      }
    },
    [value, numericDays, hasLastDay, onChange, disabled, multiple]
  );

  // Handle "last day" toggle
  const handleLastDayToggle = useCallback(
    (checked: boolean) => {
      if (disabled) return;

      if (multiple) {
        if (checked) {
          onChange([...value.filter((d) => d !== "last"), "last"]);
        } else {
          onChange(value.filter((d) => d !== "last"));
        }
      } else {
        onChange(checked ? ["last"] : []);
      }
    },
    [value, onChange, disabled, multiple]
  );

  // Calculate button size based on size prop
  const buttonSize = useMemo(() => {
    const sizes = {
      xs: { width: 28, height: 28, fontSize: "xs" as const, columns: 7 },
      sm: { width: 32, height: 32, fontSize: "xs" as const, columns: 7 },
      md: { width: 36, height: 36, fontSize: "sm" as const, columns: 7 },
      lg: { width: 44, height: 44, fontSize: "sm" as const, columns: 7 },
    };
    return sizes[size];
  }, [size]);

  // Selected set for quick lookup
  const selectedSet = useMemo(() => new Set(numericDays), [numericDays]);

  return (
    <Input.Wrapper
      label={label}
      description={description}
      error={error}
      required={required}
      size={size}
    >
      <Stack gap="sm" mt={label || description ? "xs" : 0}>
        {/* Days grid */}
        <SimpleGrid cols={buttonSize.columns} spacing="xs">
          {MONTH_DAYS.map((day) => {
            const isSelected = selectedSet.has(day);

            return (
              <UnstyledButton
                key={day}
                onClick={() => handleDayClick(day)}
                disabled={disabled}
                style={{
                  width: buttonSize.width,
                  height: buttonSize.height,
                  borderRadius: theme.radius.sm,
                  border: `1px solid ${isSelected
                    ? theme.colors.blue[6]
                    : error
                      ? theme.colors.red[6]
                      : theme.colors.gray[4]
                    }`,
                  backgroundColor: isSelected
                    ? theme.colors.blue[6]
                    : "transparent",
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: disabled ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 150ms ease",
                }}
              >
                <Text
                  size={buttonSize.fontSize}
                  fw={isSelected ? 600 : 400}
                  c={isSelected ? "white" : "dimmed"}
                >
                  {day}
                </Text>
              </UnstyledButton>
            );
          })}
        </SimpleGrid>

        {/* Last day of month option */}
        {showLastDay && (
          <Box>
            <Switch
              label={t("common:selectors.monthDaySelector.lastDayOfMonth")}
              checked={hasLastDay}
              onChange={(event) =>
                handleLastDayToggle(event.currentTarget.checked)
              }
              disabled={disabled}
              size={size === "lg" ? "md" : size === "xs" ? "xs" : "sm"}
            />
          </Box>
        )}
      </Stack>
    </Input.Wrapper>
  );
};

export const MonthDaySelector = React.memo(MonthDaySelectorComponent);